// Ported from vlookup-dramatic/app/how-it-works/page.tsx by scripts/revamp/port-design.js
import React from "react";
import Image from "../../components/image";
import Link from "../../components/link";
import { ArrowDown, ArrowRight, Briefcase, Users } from "../../lib/icons";
import { ButtonLink } from "../../components/button-link";
import { Icon } from "../../components/icon";
import { cn } from "../../lib/utils";
import { hiwHeroV3, menteeFramework, mentorFramework, platformFeatures, matchingPeople, methodology, programme, } from "../../lib/content";
import { ProgrammeTimeline } from "./programme-timeline";
import { Reveal } from "./reveal";
import { usePageMeta } from "../../hooks/use-page-meta";
import { useHashScrollOnMount } from "../../lib/section-nav";
import { isJoinHref } from "../../lib/signup-modal";
import { useLoggedIn } from "../../hooks/use-logged-in";
export default function HowItWorksPage() {
    usePageMeta({
        title: "How It Works",
        description: hiwHeroV3.sub,
    });
    useHashScrollOnMount();
    return (<div>
      <style>{`
        .hiw-reveal{opacity:0;transform:translateY(22px);transition:opacity .65s ease-out,transform .65s cubic-bezier(.22,1,.36,1)}
        .hiw-reveal.hiw-in{opacity:1;transform:translateY(0)}
        .hiw-bar{width:0%;transition:width 1.1s cubic-bezier(.22,1,.36,1) .25s}
        .hiw-in .hiw-bar{width:var(--hiw-w)}
        @keyframes hiwFloat{0%,100%{transform:translateY(0)}50%{transform:translateY(-10px)}}
        .prog-card{transition:box-shadow .5s ease}
        @media (min-width:1024px){
          /* !important + extra specificity: the row also carries md:grid-cols-3, and the scoped utilities are !important */
          .vl-revamp .prog-track.prog-track{grid-template-columns:var(--prog-cols) !important;transition:grid-template-columns .85s cubic-bezier(.22,1,.36,1)}
        }
        @media (prefers-reduced-motion: reduce){
          .hiw-reveal{opacity:1 !important;transform:none !important;transition:none !important}
          .hiw-bar{width:var(--hiw-w) !important;transition:none !important}
          .prog-track,.prog-card{transition:none !important}
        }
      `}</style>
      <Hero />
      <nav aria-label="How it works sections" className="sticky top-[76px] z-40 border-y border-neutral-200 bg-white/85 backdrop-blur-md">
        <div className="wrap flex items-center gap-1 overflow-x-auto py-2.5 text-sm">
          {[
            { label: "For mentees & mentors", href: "#frameworks" },
            { label: "The platform", href: "#platform" },
            { label: "Matching", href: "#methodology" },
            { label: "4-month programme", href: "#programme" },
        ].map((l) => (<Link key={l.href} href={l.href} className="shrink-0 rounded-full px-4 py-1.5 font-bold text-neutral-500 transition-colors hover:bg-brand-navy/5 hover:text-brand-navy">
              {l.label}
            </Link>))}
          <Link href="/find-a-mentor" className="ml-auto hidden shrink-0 rounded-full bg-brand-red px-4 py-1.5 font-bold text-white transition-colors hover:bg-brand-red-dark md:inline-block">
            Find a Mentor
          </Link>
        </div>
      </nav>
      <CombinedFrameworks />
      <PlatformFeatures />
      <MatchingPeople />
      <Programme />
    </div>);
}
/* ─────────────────────────────  HERO  ───────────────────────────── */
function Hero() {
    return (<section className="relative isolate overflow-hidden">
      <div className="pointer-events-none absolute -left-48 top-20 -z-10 size-[420px] rounded-full bg-brand-blue/15 blur-3xl"/>
      <div className="pointer-events-none absolute -right-32 bottom-0 -z-10 size-[360px] rounded-full bg-brand-blue/15 blur-3xl"/>

      {/* QA row 41: photo sits beside the copy from md up (was lg) — stacking a
          4:5 photo under the text made tablets scroll forever. */}
      <div className="wrap grid items-center gap-10 py-12 md:grid-cols-[minmax(0,7fr)_minmax(0,5fr)] md:py-14 lg:py-16 lg:gap-16">
        {/* copy */}
        <div>
          <Eyebrow>How it works</Eyebrow>
          <h1 className="mt-5 max-w-2xl font-display text-5xl font-semibold leading-[0.95] tracking-tight text-heading sm:text-6xl xl:text-7xl">
            <AccentTitle text={hiwHeroV3.title} accent="starts here."/>
          </h1>
          <p className="mt-6 max-w-xl text-lg leading-relaxed text-muted-foreground">
            {hiwHeroV3.sub}
          </p>
          <div className="mt-8 flex flex-wrap items-center gap-4">
            <AnchorPill href={hiwHeroV3.ctas[0].href} className="bg-brand-red text-white hover:bg-brand-red-dark" circleClass="bg-white/20">
              {hiwHeroV3.ctas[0].label}
            </AnchorPill>
            <AnchorPill href={hiwHeroV3.ctas[1].href} className="border border-brand-navy/25 bg-transparent text-brand-navy hover:bg-brand-navy/5" circleClass="bg-brand-navy/10">
              {hiwHeroV3.ctas[1].label}
            </AnchorPill>
          </div>
        </div>

        {/* photograph */}
        <div className="relative mx-auto w-full max-w-[380px] md:max-w-none">
          <div className="relative aspect-[4/5] overflow-hidden rounded-[2.5rem] rounded-tr-[6rem] shadow-[0_40px_90px_-50px_rgba(26,58,92,0.55)]">
            <Image src="/revamp/images/hiw-hero.webp" alt="Mentors and mentees on stage at a vLookUp programme event" fill width={960} height={1200} priority sizes="(min-width: 1024px) 38vw, 90vw" className="object-cover"/>
          </div>
          <div className="absolute -left-4 top-10 animate-[hiwFloat_6s_ease-in-out_infinite] motion-reduce:animate-none">
            <FloatChip icon="target" label="Goal-first matching"/>
          </div>
          <div className="absolute -right-4 top-28 animate-[hiwFloat_7.5s_ease-in-out_0.6s_infinite] motion-reduce:animate-none">
            <FloatChip icon="choice" label="Set preferences"/>
          </div>
          <div className="absolute -left-6 bottom-28 animate-[hiwFloat_6.5s_ease-in-out_1.8s_infinite] motion-reduce:animate-none">
            <FloatChip icon="keyperson" label="Choose your mentor"/>
          </div>
          <div className="absolute -right-3 bottom-12 animate-[hiwFloat_7s_ease-in-out_1.2s_infinite] motion-reduce:animate-none">
            <FloatChip icon="calendar" label="4-month programme"/>
          </div>
        </div>
      </div>
    </section>);
}
/* ─────────────  GROWTH FRAMEWORK / MENTOR JOURNEY  ─────────────── */
const frameworkTones = {
    mentee: {
        pill: "text-brand-red",
        accent: "text-brand-red",
        link: "text-brand-red",
        glow: "bg-brand-blue/15",
        icon: Users,
    },
    mentor: {
        pill: "text-brand-blue",
        accent: "text-brand-blue",
        link: "text-brand-blue",
        glow: "bg-brand-blue/15",
        icon: Briefcase,
    },
};
// Per-step call-to-action labels — only the steps that carry an href.
const stepLinkLabels = {
    mentees: { 1: "Create your profile", 2: "Browse mentors", 5: "Join us on LinkedIn" },
    mentors: { 1: "Become a mentor", 5: "Follow us on LinkedIn" },
};
function CombinedFrameworks() {
    return (<section id="frameworks" className="relative isolate scroll-mt-32 overflow-hidden border-t border-brand-navy/10 py-14 md:py-20">
      <div className="wrap">
        <Reveal>
          <h2 className="max-w-3xl font-display text-4xl font-semibold leading-[1.05] text-heading md:text-5xl">
            How it comes {" "}
            <em className="font-normal italic text-brand-red">together</em>
          </h2>
        </Reveal>

        <div className="mt-10 overflow-hidden rounded-[2.5rem] bg-card shadow-sm ring-1 ring-brand-navy/10 lg:grid lg:grid-cols-2">
          <FrameworkColumn data={menteeFramework} variant="mentee" accent="Growth" kicker="Five steps — from defining your goals to growing beyond them."/>
          <FrameworkColumn data={mentorFramework} variant="mentor" accent="Journey" kicker="Five steps — from sharing your story to shaping someone else's." last/>
        </div>
      </div>
    </section>);
}
function FrameworkColumn({ data, variant, accent, kicker, last, }) {
    const t = frameworkTones[variant];
    const PillIcon = t.icon;
    const labels = stepLinkLabels[data.id] ?? {};
    return (<div id={data.id} className={cn("relative scroll-mt-32 px-6 py-10 md:px-10 md:py-12", last
            ? "bg-cream-deep lg:border-l lg:border-brand-navy/10"
            : "border-b border-brand-navy/10 lg:border-b-0")}>
      <div aria-hidden className={cn("pointer-events-none absolute -right-24 -top-24 size-[280px] rounded-full blur-3xl", t.glow)}/>
      <span className="relative inline-flex items-center gap-2.5 rounded-full bg-white px-4 py-2 text-xs font-bold uppercase tracking-[0.18em] text-brand-navy shadow-sm ring-1 ring-brand-navy/10">
        <PillIcon className={cn("size-4", t.pill)}/>
        {data.tag}
      </span>
      <h3 className="relative mt-5 font-display text-3xl font-semibold leading-[1.05] text-heading md:text-4xl">
        <AccentTitle text={data.heading} accent={accent} accentClass={t.accent}/>
      </h3>
      <p className="relative mt-3 max-w-md text-sm leading-relaxed text-muted-foreground">
        {kicker}
      </p>

      <ul className="relative mt-8 flex flex-col">
        {data.steps.map((step, i) => (<StepRow key={step.n} step={step} tone={t} index={i} linkLabel={labels[step.n]}/>))}
      </ul>
    </div>);
}
function StepRow({ step, tone, index, linkLabel, }) {
    const loggedIn = useLoggedIn();
    const rawHref = "href" in step ? step.href : undefined;
    const href = loggedIn && isJoinHref(rawHref) ? undefined : rawHref;
    const external = href?.startsWith("http") ?? false;
    const body = (<span className="flex items-start gap-4">
      <Icon name={step.icon} className="size-12 shrink-0 rounded-xl"/>
      <span className="min-w-0">
        <span className="block font-display text-lg font-semibold text-heading">
          {step.title}
        </span>
        <span className="mt-1 block text-sm leading-relaxed text-muted-foreground">
          {step.desc}
        </span>
        {href && linkLabel && (<span className={cn("mt-2 inline-flex items-center gap-1.5 text-sm font-bold", tone.link)}>
            {linkLabel}
            <ArrowRight className="size-3.5 transition-transform duration-300 group-hover:translate-x-0.5"/>
          </span>)}
      </span>
    </span>);
    const rowCls = cn("group py-5", index > 0 && "border-t border-brand-navy/8");
    if (!href) {
        return <li className={rowCls}>{body}</li>;
    }
    return (<li>
      {external ? (<a href={href} target="_blank" rel="noopener noreferrer" className={cn(rowCls, "rounded-2xl transition-colors hover:bg-white/60")}>
          {body}
        </a>) : (<Link href={href} className={cn(rowCls, "rounded-2xl transition-colors hover:bg-white/60")}>
          {body}
        </Link>)}
    </li>);
}
function PlatformFeatures() {
    return (<section id="platform" className="relative isolate scroll-mt-32 overflow-hidden border-t border-brand-navy/10 py-14 md:py-20">

      <div className="wrap">
        <Reveal className="flex flex-wrap items-end justify-between gap-6">
          <div>
            <Eyebrow>{platformFeatures.eyebrow}</Eyebrow>
            <h2 className="mt-5 max-w-xl font-display text-4xl font-semibold leading-[1.02] text-heading md:text-5xl">
              <AccentTitle text={platformFeatures.heading} accent="supported."/>
            </h2>
          </div>
          <p className="max-w-md text-base leading-relaxed text-muted-foreground">
            {platformFeatures.sub}
          </p>
        </Reveal>

        <div className="mt-10 grid gap-4 sm:grid-cols-2 md:gap-5 lg:grid-cols-3">
          {platformFeatures.items.map((item, i) => (<Reveal as="article" key={item.title} delay={i * 60} className="group relative overflow-hidden rounded-3xl bg-card p-6 shadow-sm ring-1 ring-brand-navy/5 transition-all duration-300 hover:-translate-y-1 hover:shadow-xl">
              <Icon name={item.icon} className="size-12 rounded-xl"/>
              <h3 className="mt-4 font-display text-xl font-semibold text-heading">
                {item.title}
              </h3>
              <p className="mt-1.5 text-sm leading-relaxed text-muted-foreground">
                {item.desc}
              </p>
            </Reveal>))}
        </div>
      </div>
    </section>);
}
/* ──────  MATCHING BUILT AROUND PEOPLE (+ kept match scorecard)  ───── */
function MatchingPeople() {
    const { rows, overall, overallLabel, title } = methodology.score;
    const [overallPct, overallResult] = overall.split("—").map((s) => s.trim());
    return (<section id={matchingPeople.id} className="relative isolate scroll-mt-32 overflow-hidden border-y border-brand-navy/10 bg-cream-deep py-14 md:py-20">
      <div className="pointer-events-none absolute -right-24 -top-28 -z-10 size-[420px] rounded-full bg-brand-blue/15 blur-3xl"/>
      <div className="pointer-events-none absolute -bottom-28 -left-20 -z-10 size-[360px] rounded-full bg-brand-blue/15 blur-3xl"/>

      <div className="wrap">
        <Reveal className="max-w-2xl">
          <Eyebrow>{matchingPeople.eyebrow}</Eyebrow>
          <h2 className="mt-5 font-display text-4xl font-semibold leading-[1.02] text-heading md:text-5xl">
            <AccentTitle text={matchingPeople.heading} accent="people" accentClass="text-brand-red"/>
          </h2>
          <p className="mt-5 text-base leading-relaxed text-muted-foreground md:text-lg">
            {matchingPeople.sub}
          </p>
        </Reveal>

        <div className="mt-10 grid gap-8 lg:grid-cols-2 lg:items-center lg:gap-12">
          {/* the four human signals */}
          <div className="grid gap-4 sm:grid-cols-2">
            {matchingPeople.items.map((item, i) => (<Reveal as="article" key={item.title} delay={i * 70} className="rounded-2xl bg-card p-5 shadow-sm ring-1 ring-brand-navy/5 transition-shadow hover:shadow-lg md:p-6">
                <Icon name={item.icon} className="size-11 rounded-xl"/>
                <h3 className="mt-4 font-display text-lg font-semibold text-heading">
                  {item.title}
                </h3>
                <p className="mt-1.5 text-sm leading-relaxed text-muted-foreground">
                  {item.desc}
                </p>
              </Reveal>))}
          </div>

          {/* KEPT: match score breakdown card */}
          <Reveal delay={120} className="rounded-[2rem] bg-card p-6 text-brand-navy shadow-xl ring-1 ring-brand-navy/5 md:p-9">
            <p className="text-xs font-bold uppercase tracking-[0.2em] text-brand-red">
              {title}
            </p>
            <div className="mt-6 flex flex-col gap-5">
              {rows.map((row) => (<div key={row.label}>
                  <div className="flex items-center justify-between text-sm font-bold">
                    <span className="text-brand-navy">{row.label}</span>
                    <span className="text-brand-red">{row.pct}%</span>
                  </div>
                  <div className="mt-2 h-2.5 w-full overflow-hidden rounded-full bg-cream-deep">
                    <div className="hiw-bar h-full rounded-full bg-brand-blue" style={{ "--hiw-w": `${row.pct}%` }}/>
                  </div>
                </div>))}
            </div>
            {/* QA row 44: label bottom-aligned with "Strong Match" */}
            <div className="mt-7 flex flex-wrap items-end justify-between gap-4 rounded-2xl bg-brand-red px-6 py-5 text-white">
              <div className="flex items-baseline gap-3">
                <span className="font-display text-5xl font-semibold leading-none">
                  {overallPct}
                </span>
                <span className="font-display text-lg font-semibold">
                  {overallResult}
                </span>
              </div>
              <span className="pb-0.5 text-[11px] font-bold uppercase tracking-[0.18em] text-white/70">
                {overallLabel}
              </span>
            </div>
          </Reveal>
        </div>
      </div>
    </section>);
}
/* ─────────────────  KEPT: 4-MONTH PROGRAMME  ──────────────────── */
function Programme() {
    return (<section id="programme" className="relative isolate scroll-mt-32 overflow-hidden py-14 md:py-20">

      <div className="wrap">
        <Reveal className="max-w-2xl">
          <Eyebrow>{programme.label}</Eyebrow>
          <h2 className="mt-5 font-display text-4xl font-semibold leading-[1.02] text-heading md:text-5xl">
            <AccentTitle text={programme.heading} accent="real progress"/>
          </h2>
          <p className="mt-5 text-base leading-relaxed text-muted-foreground md:text-lg">
            {programme.sub}
          </p>
        </Reveal>

        <Reveal className="mt-10">
          <ProgrammeTimeline />
        </Reveal>
      </div>
    </section>);
}
/* ───────────────────  SLIM FAQ LINK BAND  ─────────────────────── */
/* ───────────────────  CLOSING CTA  ────────────────────────────── */
/* ─────────────────────────────  HELPERS  ─────────────────────────── */
function Eyebrow({ children, className, }) {
    return (<span className={cn("inline-flex items-center gap-2.5 text-xs font-bold uppercase tracking-[0.2em] text-brand-red", className)}>
      <span className="h-px w-8 bg-current opacity-50"/>
      {children}
    </span>);
}
function AccentTitle({ text, accent, accentClass = "text-brand-red", }) {
    const idx = text.indexOf(accent);
    if (idx === -1)
        return <>{text}</>;
    return (<>
      {text.slice(0, idx)}
      <em className={cn("font-normal italic", accentClass)}>{accent}</em>
      {text.slice(idx + accent.length)}
    </>);
}
// Pill CTA for in-page anchors — arrow points down to the target section.
function AnchorPill({ href, children, className, circleClass, }) {
    return (<ButtonLink href={href} className={cn("group h-auto w-fit gap-3 rounded-full py-2 pl-6 pr-2 text-[15px] font-semibold", className)}>
      {children}
      <span className={cn("flex size-9 items-center justify-center rounded-full transition-transform group-hover:translate-y-0.5", circleClass)}>
        <ArrowDown className="size-4.5"/>
      </span>
    </ButtonLink>);
}
function FloatChip({ icon, label }) {
    return (<div className="hiw-float-chip flex items-center gap-1.5 rounded-full bg-card px-2.5 py-1.5 shadow-lg ring-1 ring-brand-navy/5">
      <Icon name={icon} size={18} className="size-4 rounded-md"/>
      <span className="text-[10px] font-bold leading-none text-brand-navy">{label}</span>
    </div>);
}
